import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { GridIcon, LayoutIcon, SlidersIcon, StarIcon, CheckCircleIcon } from "lucide-react";
import ProductCard from "@/polymet/components/product-card";
import { Product } from "@/polymet/data/machinery-products";
import { useAuth } from "@/lib/auth-context";
import { useToast } from "@/hooks/use-toast";
import { RFQService } from "@/lib/rfq-service";
import { useAnalytics } from "@/polymet/hooks/use-analytics";

interface ProductGridProps {
  products: Product[];
  title?: string;
  showFilters?: boolean;
  className?: string;
  enableDirectQuotes?: boolean;
}

export default function ProductGrid({
  products,
  title,
  showFilters = true,
  className = "",
  enableDirectQuotes = false,
}: ProductGridProps) {
  const [sortBy, setSortBy] = useState("featured");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [requestingQuotes, setRequestingQuotes] = useState<Set<string>>(new Set());
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successDialogData, setSuccessDialogData] = useState<{
    productName: string;
    supplierName?: string;
  } | null>(null);

  // Hooks
  const { user } = useAuth();
  const { toast } = useToast();
  const { trackProductVisit } = useAnalytics();
  const rfqService = new RFQService();
  const navigate = useNavigate();

  const sortedProducts = [...products].sort((a, b) => {
    switch (sortBy) {
      case "price-low":
        return a.price - b.price;
      case "price-high":
        return b.price - a.price;
      case "newest":
        return (
          new Date(b.listedDate).getTime() - new Date(a.listedDate).getTime()
        );

      case "rating":
        return b.rating - a.rating;
      case "featured":
      default:
        return b.featured ? 1 : -1;
    }
  });

  // Handle direct quote request
  const handleDirectQuoteRequest = async (product: Product) => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please log in to request quotes.",
        variant: "destructive",
      });
      return;
    }

    if (requestingQuotes.has(product.id)) {
      return; // Already processing
    }

    setRequestingQuotes(prev => new Set(prev).add(product.id));

    try {
      // Create RFQ with prefilled product data
      const rfqData = {
        user_id: user.id,
        title: `Quote Request for ${product.name}`,
        description: `I am interested in getting a quote for the ${product.manufacturer} ${product.model} ${product.name}${product.year ? ` (${product.year})` : ''} in ${product.condition} condition. Please provide your best price and terms.`,
        request_type: 'quote_request' as const,
        status: 'published' as const,
        category_id: product.categoryId || undefined,
        subcategory_id: product.subCategoryId || undefined,
        product_category_id: product.productCategoryId || undefined,
        quantity: 1,
        budget_min: product.price ? Math.floor(product.price * 0.8) : undefined, // 20% below listed price
        budget_max: product.price ? Math.ceil(product.price * 1.1) : undefined, // 10% above listed price
        target_price: product.price?.toString() || undefined,
        delivery_location: undefined,
        delivery_date: undefined,
        specifications: {
          'Manufacturer': product.manufacturer,
          'Model': product.model,
          'Year': product.year?.toString() || 'N/A',
          'Condition': product.condition,
          'Listed Price': `${product.price} ${product.currency || 'USD'}`
        },
        attachments: [],
        supplier_id: product.seller?.id || undefined,
      };

      const result = await rfqService.createRFQ(rfqData);

      // Track the RFQ interaction
      try {
        await trackProductVisit({
          productId: product.id,
          supplierId: product.seller?.id,
          categoryId: product.categoryId,
          subcategoryId: product.subCategoryId,
          productCategoryId: product.productCategoryId,
          source: 'category',
          interactionType: 'rfq',
          timeSpent: 30 // Estimate 30 seconds for quick quote
        });
      } catch (trackingError) {
        console.error('Error tracking RFQ interaction:', trackingError);
      }

      // Show success dialog instead of toast
      setSuccessDialogData({
        productName: product.name,
        supplierName: product.seller?.name
      });
      setShowSuccessDialog(true);

    } catch (error) {
      console.error('Error creating direct quote request:', error);
      toast({
        title: "Error",
        description: "Failed to send quote request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setRequestingQuotes(prev => {
        const newSet = new Set(prev);
        newSet.delete(product.id);
        return newSet;
      });
    }
  };

  return (
    <div className={className}>
      {/* Success Dialog */}
      <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircleIcon className="h-5 w-5 text-green-600" />
              Quote Request Sent Successfully!
            </DialogTitle>
            <DialogDescription className="text-left">
              {successDialogData?.supplierName ? (
                <>
                  Your quote request for <strong>{successDialogData.productName}</strong> has been sent to{" "}
                  <strong>{successDialogData.supplierName}</strong>.
                </>
              ) : (
                <>
                  Your quote request for <strong>{successDialogData?.productName}</strong> has been submitted successfully.
                </>
              )}
              <br /><br />
              You will receive notifications when suppliers respond to your request. You can track all your quote requests in your dashboard.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowSuccessDialog(false)}>
              Continue Shopping
            </Button>
            <Button onClick={() => {
              setShowSuccessDialog(false);
              // Navigate to user's RFQ dashboard (you can implement this)
              navigate('/buyer-dashboard/rfqs');
            }}>
              View My Requests
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {title && <h2 className="text-xl font-bold mb-4">{title}</h2>}

      {showFilters && (
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6 bg-muted/30 p-4 rounded-lg">
          <div className="flex items-center">
            <span className="text-sm font-medium mr-2">
              {products.length} {products.length === 1 ? "Product" : "Products"}
            </span>
          </div>

          <div className="flex items-center gap-3 ml-auto">
            <div className="flex items-center">
              <span className="text-sm mr-2 hidden sm:inline">Sort by:</span>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="featured">Featured</SelectItem>
                  <SelectItem value="price-low">Price: Low to High</SelectItem>
                  <SelectItem value="price-high">Price: High to Low</SelectItem>
                  <SelectItem value="newest">Newest First</SelectItem>
                  <SelectItem value="rating">Highest Rated</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center border rounded-md">
              <Button
                variant={viewMode === "grid" ? "default" : "ghost"}
                size="icon"
                className="h-9 w-9 rounded-none rounded-l-md"
                onClick={() => setViewMode("grid")}
              >
                <GridIcon className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === "list" ? "default" : "ghost"}
                size="icon"
                className="h-9 w-9 rounded-none rounded-r-md"
                onClick={() => setViewMode("list")}
              >
                <LayoutIcon className="h-4 w-4" />
              </Button>
            </div>

            <Button
              variant="outline"
              size="sm"
              className="md:hidden"
              onClick={() => setFiltersVisible(!filtersVisible)}
            >
              <SlidersIcon className="h-4 w-4 mr-2" />
              Filters
            </Button>
          </div>
        </div>
      )}

      {viewMode === "grid" ? (
        <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {sortedProducts.map((product) => (
            <ProductCard
              key={product.id}
              id={product.id}
              slug={product.slug}
              name={product.name}
              condition={product.condition}
              price={product.price}
              previousPrice={product.previousPrice}
              currency={product.currency}
              image={product.images[0]}
              year={product.year}
              manufacturer={product.manufacturer}
              model={product.model}
              location={product.location}
              rating={product.rating}
              reviewCount={product.reviewCount}
              featured={product.featured}
              categoryId={product.categoryId}
              subCategoryId={product.subCategoryId}
              productCategoryId={product.productCategoryId}
              supplierName={product.seller?.name}
              supplierId={product.seller?.id}
              enableDirectQuote={enableDirectQuotes}
              onDirectQuoteRequest={enableDirectQuotes ? () => handleDirectQuoteRequest(product) : undefined}
              isRequestingQuote={requestingQuotes.has(product.id)}
            />
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {sortedProducts.map((product) => (
            <div
              key={product.id}
              className="flex flex-col sm:flex-row gap-4 border rounded-lg overflow-hidden hover:shadow-md transition-shadow"
            >
              <div className="sm:w-64 h-48 sm:h-auto relative">
                <img
                  src={product.images[0]}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />

                {product.featured && (
                  <div className="absolute top-2 left-2">
                    <span className="bg-primary text-primary-foreground text-xs px-2 py-1 rounded-md">
                      Featured
                    </span>
                  </div>
                )}
                {product.condition && (
                  <div className="absolute bottom-2 left-2">
                    <span
                      className={`text-xs px-2 py-1 rounded-full ${
                        product.condition === "New"
                          ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
                          : product.condition === "Used"
                            ? "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300"
                            : "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300"
                      }`}
                    >
                      {product.condition}
                    </span>
                  </div>
                )}
              </div>

              <div className="flex-1 p-4 flex flex-col">
                <div className="mb-2">
                  <h3 className="font-medium text-lg mb-1">
                    <a
                      href={`/product/${product.id}`}
                      className="hover:underline"
                    >
                      {product.name}
                    </a>
                  </h3>

                  <div className="flex items-center text-sm text-muted-foreground">
                    <span className="font-medium">{product.manufacturer}</span>
                    <span className="mx-1">•</span>
                    <span>{product.model}</span>
                    {product.year && (
                      <>
                        <span className="mx-1">•</span>
                        <span>{product.year}</span>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex items-center text-sm text-muted-foreground mb-2">
                  <span>{product.location}</span>
                </div>

                <div className="flex items-center mb-4">
                  <div className="flex items-center">
                    <StarIcon className="h-4 w-4 text-amber-500 mr-1" />

                    <span className="font-medium text-sm">
                      {product.rating.toFixed(1)}
                    </span>
                  </div>
                  <span className="mx-1.5 text-muted-foreground text-xs">
                    •
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {product.reviewCount}{" "}
                    {product.reviewCount === 1 ? "review" : "reviews"}
                  </span>
                </div>

                <div className="mt-auto flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <span className="text-xl font-bold">
                      {new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: product.currency || "USD",
                        maximumFractionDigits: 0,
                      }).format(product.price)}
                    </span>
                    {product.previousPrice &&
                      product.previousPrice > product.price && (
                        <span className="ml-2 text-sm text-muted-foreground line-through">
                          {new Intl.NumberFormat("en-US", {
                            style: "currency",
                            currency: product.currency || "USD",
                            maximumFractionDigits: 0,
                          }).format(product.previousPrice)}
                        </span>
                      )}
                  </div>

                  <div className="flex gap-2">
                    <Button variant="outline" size="sm">
                      Add to Cart
                    </Button>
                    <Button size="sm">View Details</Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {sortedProducts.length === 0 && (
        <div className="text-center py-12">
          <p className="text-lg text-muted-foreground">No products found</p>
        </div>
      )}
    </div>
  );
}
